// src/hooks/useNewFaxes.js
// Checks for faxes newer than the newest one in the list (Outgoing or
// Incoming tab), using the filters of the last search, every 20 seconds
// while the page is visible.
//
// It never reloads or reorders what the user is looking at. New faxes are
// only ever ADDED:
//   - straight into the top of the list when the user is idle there
//     (isBusy() is false: at the top, nothing selected, nothing open), or
//   - held as "N new faxes · Show" (pending) while the user is busy, and
//     added when they click Show or become idle.
// Filters, typed search text, selections, scroll position and any open
// panel or dialog are left untouched either way.
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchOutgoing, fetchIncoming } from '../utils/api';

export const NEW_FAX_POLL_MS = 20 * 1000;
const MIN_GAP_MS  = 5 * 1000;
const EMPTY_LOOKBACK_MS = 2 * 60 * 1000;   // empty list: look back from the search time

const timeField = tab => (tab === 'outgoing' ? 'sentAt' : 'receivedAt');

function sortNewestFirst(rows, field) {
  return rows.sort((a, b) => (b[field] || '').localeCompare(a[field] || ''));
}

export function useNewFaxes({ tab, enabled, data, setData, appliedFilters, getToken, isBusy, onAdded, onFound }) {
  const [pending, setPending] = useState([]);
  const dataRef     = useRef(data);
  const pendingRef  = useRef(pending);
  const filtersRef  = useRef(appliedFilters);
  const tokenRef    = useRef(getToken);
  const busyRef     = useRef(isBusy);
  const addedRef    = useRef(onAdded);
  const foundRef    = useRef(onFound);
  const lastRef     = useRef(0);
  const inFlightRef = useRef(false);
  const searchedAt  = useRef(new Date().toISOString());

  dataRef.current    = data;
  pendingRef.current = pending;
  filtersRef.current = appliedFilters;
  tokenRef.current   = getToken;
  busyRef.current    = isBusy;
  addedRef.current   = onAdded;
  foundRef.current   = onFound;

  // A new search (or tab) starts over
  useEffect(() => {
    setPending([]);
    searchedAt.current = new Date().toISOString();
  }, [appliedFilters, tab]);

  // Put rows into the list (skipping any already there), newest first
  const addToList = useCallback(rows => {
    if (!rows.length) return;
    const field = timeField(tab);
    setData(prev => {
      const have  = new Set(prev.map(r => r.faxId));
      const fresh = rows.filter(r => !have.has(r.faxId));
      return fresh.length ? sortNewestFirst([...fresh, ...prev], field) : prev;
    });
    addedRef.current?.(rows.map(r => r.faxId));
  }, [tab, setData]);

  // "Show" on the new-faxes bar
  const showPending = useCallback(() => {
    const rows = pendingRef.current;
    setPending([]);
    addToList(rows);
  }, [addToList]);

  const check = useCallback(async ({ force = false } = {}) => {
    const filters = filtersRef.current;
    if (!filters || inFlightRef.current || document.hidden) return;
    if (!force && Date.now() - lastRef.current < MIN_GAP_MS) return;
    inFlightRef.current = true;
    lastRef.current = Date.now();
    try {
      const field  = timeField(tab);
      const newest = dataRef.current.reduce((m, r) => ((r[field] || '') > m ? r[field] : m), '');
      const after  = newest || new Date(Date.parse(searchedAt.current) - EMPTY_LOOKBACK_MS).toISOString();
      const fetcher = tab === 'outgoing' ? fetchOutgoing : fetchIncoming;
      const result  = await fetcher({ ...filters, after }, () => tokenRef.current());
      if (filtersRef.current !== filters) return;   // a new search started meanwhile

      const known = new Set([...dataRef.current, ...pendingRef.current].map(r => r.faxId));
      const found = (result.data || []).filter(r => !known.has(r.faxId));
      if (found.length) foundRef.current?.(found);

      const waiting = [...found, ...pendingRef.current];
      if (!waiting.length) return;
      if (busyRef.current?.()) {
        setPending(sortNewestFirst(waiting, field));
      } else {
        setPending([]);
        addToList(waiting);
      }
    } catch {
      // Transient errors: try again on the next check
    } finally {
      inFlightRef.current = false;
    }
  }, [tab, addToList]);

  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => check(), NEW_FAX_POLL_MS);
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, check]);

  return { pending, showPending, checkNow: () => check({ force: true }) };
}
